export const DEBUG_ENABLED =
  typeof window !== "undefined" &&
  (typeof process !== "undefined"
    ? process.env.VITE_ENABLE_DEBUG_LOGS === "true" || process.env.VITE_ENABLE_DEBUG_LOGS === "1"
    : false);

export function isBizDebugEnabled(): boolean {
  return DEBUG_ENABLED;
}

export const logger = {
  debug(message: string, data?: unknown) {
    if (!DEBUG_ENABLED) return;

    if (data === undefined) {
      console.debug(message);
    } else {
      console.debug(message, redactSensitive(data));
    }
  },

  info(message: string, data?: unknown) {
    if (!DEBUG_ENABLED) return;

    if (data === undefined) {
      console.info(message);
    } else {
      console.info(message, redactSensitive(data));
    }
  },

  warn(message: string, data?: unknown) {
    if (data === undefined) {
      console.warn(message);
    } else {
      console.warn(message, redactSensitive(data));
    }
  },

  error(message: string, error?: unknown) {
    if (error === undefined) {
      console.error(message);
    } else {
      console.error(message, redactSensitive(error));
    }
  }
};

export function bizDebug(label: string, ...args: any[]) {
  if (DEBUG_ENABLED) {
    logger.debug(`[BIZ_DEBUG] ${label}`, args.length === 1 ? args[0] : args.length > 0 ? args : undefined);
  }
}

export function bizError(label: string, ...args: any[]) {
  const cleanArgs = args.map(arg => redactSensitive(arg));
  console.error(`[BIZ_ERROR] ${label}`, ...cleanArgs);
}

function maskEmail(email: string): string {
  const parts = email.split("@");
  if (parts.length !== 2) return "[REDACTED_EMAIL]";
  const name = parts[0];
  const domain = parts[1];
  if (name.length <= 2) {
    return `${name[0]}***@${domain}`;
  }
  return `${name[0]}***${name[name.length - 1]}@${domain}`;
}

function redactSensitive(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === "string") {
    const trimmed = obj.trim();
    // Check if it's a UUID
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
      return `${trimmed.slice(0, 8)}-***`;
    }
    // Sanitize values that look like secrets (e.g. Bearer, jwt starting with eyJ, sb-..., or length >= 30)
    if (trimmed.length > 25 && (
      trimmed.startsWith("Bearer ") || 
      trimmed.startsWith("eyJ") || 
      /sb-[a-zA-Z0-9_-]+/.test(trimmed) ||
      /^[a-zA-Z0-9_-]{30,}$/.test(trimmed)
    )) {
      return "[REDACTED_SECRET]";
    }
    // Check if the string itself is an email
    if (trimmed.includes("@") && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return maskEmail(trimmed);
    }
    return obj;
  }
  if (typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map(redactSensitive);
  }

  // Detect and scrub full Supabase user or session objects
  if (obj && (obj.access_token || obj.refresh_token || (obj.user && obj.user.id))) {
    return {
      userId: obj.user?.id ? `${obj.user.id.slice(0, 8)}-***` : "[REDACTED]",
      email: obj.user?.email ? maskEmail(obj.user.email) : "[REDACTED]",
      hasAccessToken: Boolean(obj.access_token),
      hasRefreshToken: Boolean(obj.refresh_token)
    };
  }

  // Detect and scrub full user objects
  if (obj && obj.id && (obj.email || obj.user_metadata)) {
    return {
      id: `${obj.id.slice(0, 8)}-***`,
      email: obj.email ? maskEmail(obj.email) : undefined,
      provider: obj.provider || undefined
    };
  }

  const redacted: any = {};
  const redactedKeys = [
    "token", "access_token", "refresh_token", "authorization", "cookie", 
    "password", "secret", "api_key", "apikey", "service_role", 
    "stripe", "session", "provider_id", "sub", "email", "phone"
  ];

  const coordKeys = ["lat", "lng", "latitude", "longitude", "coords", "location"];

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const lowerKey = key.toLowerCase();
      const isRedactedKey = redactedKeys.some(rk => lowerKey === rk || lowerKey.includes(rk));
      const isCoordKey = coordKeys.some(ck => lowerKey === ck);

      if (isRedactedKey) {
        if (lowerKey === "email" && typeof obj[key] === "string") {
          redacted[key] = maskEmail(obj[key]);
        } else {
          redacted[key] = "[REDACTED]";
        }
      } else if (isCoordKey) {
        redacted[key] = "[REDACTED_COORDS]";
      } else {
        const val = obj[key];
        if (typeof val === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)) {
          redacted[key] = `${val.slice(0, 8)}-***`;
        } else {
          redacted[key] = redactSensitive(val);
        }
      }
    }
  }
  return redacted;
}
